import type { ChaveConfigPontoAcesso, ConfigPontoAcessoOverride, ConfiguracaoLoja, ModoConsumoTotem, PontoAcessoConfiguravel, TotemNota } from "@/api"
import { ROTULO_CONSUMO } from "@/api"
import { AvisoPinDispositivo } from "@/components/admin/aviso-pin-dispositivo"
import { InfoModo } from "@/components/admin/painel-info"
import { BotaoGerenciarPontosDeAcesso, PontosDeAcessoCard } from "@/components/admin/pontos-de-acesso-card"
import { UploadImagensTotem } from "@/components/admin/upload-imagens-totem"
import { CampoUsuarioNfce } from "@/components/admin/campo-usuario-nfce"
import { opcoesImpressaoTotem, totemImprimeAlgo, type OpcoesImpressaoTotem } from "@/lib/impressao-totem"
import type { AcessoModos } from "@/lib/modos-cardapio"
import { Alert, Box, Chip, Divider, FormControlLabel, MenuItem, Paper, Radio, RadioGroup, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import Grid from '@mui/material/Grid2'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import PrintIcon from '@mui/icons-material/Print'
import PaidIcon from '@mui/icons-material/Paid'
import { CampoValorReais, GateModo, OpcaoImpressaoTotem, Secao } from "."

interface SecaoTotemPedidosProps {
    modos: AcessoModos | undefined
    /** Configuração da LOJA: é o que vale no totem que ainda não definiu a sua. */
    form: Record<string, unknown>
    idConfig: number
    pontos: PontoAcessoConfiguravel[]
    idPontoSelecionado: number | null
    onSelecionarPonto: (id: number | null) => void
    overridesPontos: Record<number, ConfigPontoAcessoOverride>
    setOverridePonto: (idPonto: number, override: ConfigPontoAcessoOverride | null) => void
}

function rotuloPonto(ponto: PontoAcessoConfiguravel): string {
    return ponto.numero?.trim() || ponto.codigo
}

export default function SecaoTotemPedidos({ modos, form, idConfig, pontos, idPontoSelecionado, onSelecionarPonto, overridesPontos, setOverridePonto }: SecaoTotemPedidosProps) {
    // A maioria das lojas tem um totem só: ele já vem escolhido.
    const idAtivo = idPontoSelecionado ?? (pontos.length === 1 ? pontos[0]?.id ?? null : null)
    const pontoSelecionado = pontos.find((p) => p.id === idAtivo) ?? null
    const configAtual: ConfigPontoAcessoOverride = (pontoSelecionado ? overridesPontos[pontoSelecionado.id] ?? pontoSelecionado.configuracao : null) ?? {}

    function ler(chave: ChaveConfigPontoAcesso): unknown {
        return chave in configAtual ? configAtual[chave] : form[chave]
    }

    function escrever(parcial: ConfigPontoAcessoOverride): void {
        if (!pontoSelecionado) return
        setOverridePonto(pontoSelecionado.id, { ...configAtual, ...parcial })
    }

    const impressao = opcoesImpressaoTotem({
        totemImpressao: form.totemImpressao as ConfiguracaoLoja['totemImpressao'] | undefined,
        totemImprimirSenha: ler('totemImprimirSenha') as boolean | undefined,
        totemImprimirResumo: ler('totemImprimirResumo') as boolean | undefined,
        totemNota: ler('totemNota') as TotemNota | undefined,
    })

    // As três opções são gravadas juntas: uma só deixaria as outras duas mudando com a loja, sem o lojista ver.
    function setImpressao(parcial: Partial<OpcoesImpressaoTotem>): void {
        const opcoes = { ...impressao, ...parcial }
        escrever({ totemImprimirSenha: opcoes.imprimirSenha, totemImprimirResumo: opcoes.imprimirResumo, totemNota: opcoes.nota })
    }

    const perguntaCpf = ler('totemPerguntarCpfNota') !== false
    const comprovante = ler('totemImprimirComprovanteTef') !== false

    const consumo = (ler('totemConsumo') as ModoConsumoTotem | undefined) ?? 'AMBOS'
    const pagerAtivo = (ler('totemPagerAtivo') as boolean | undefined) ?? false
    const minimoBruto = ler('valorMinimoTotem')
    const valorMinimo = minimoBruto == null || minimoBruto === '' ? undefined : Number(minimoBruto)

    return (
        <GateModo modo="totem" modos={modos}>
            <InfoModo
                itens={[
                    <>
                        Os pedidos caem no <strong>PDV Delivery</strong> do Sischef, no mesmo lugar dos pedidos de
                        delivery, mas entram como <strong>pedido de balcão</strong> — e não como entrega. Na
                        observação vem <strong>"Pedido via TOTEM"</strong>.
                    </>,
                    <>
                        A escolha do cliente aparece no card do pedido: <strong>Consumo no local</strong> ou{' '}
                        <strong>Para retirar</strong>.
                    </>,
                    <>
                        O cliente <strong>paga no próprio totem</strong> (TEF) antes do pedido ser enviado. Só depois
                        do pagamento aprovado o pedido chega ao Sischef.
                    </>,
                    <>
                        O que sai no papel depende das opções em <strong>Impressão do pedido</strong>, abaixo: senha,
                        resumo do pedido e nota fiscal (NFC-e), em qualquer combinação. Sempre na impressora{' '}
                        <strong>acoplada a este totem</strong>, escolhida no próprio aparelho.
                    </>,
                ]}
            />

            <Secao
                icone={<QrCode2Icon />}
                titulo="Pontos de acesso"
                descricao="Totens configurados. Abra a URL no navegador do totem (modo kiosk)."
                acoes={<BotaoGerenciarPontosDeAcesso idConfig={idConfig} tipos={['TOTEM']} />}
            >
                <PontosDeAcessoCard idConfig={idConfig} tipos={['TOTEM']} />
            </Secao>

            <Secao
                icone={<PointOfSaleIcon />}
                titulo="Totem a configurar"
                descricao="Escolha o totem para editar suas configurações. Tudo abaixo é por aparelho; a impressora e o PIN ficam no próprio equipamento."
            >
                {pontos.length === 0 ? (
                    <Alert severity="info">
                        Nenhum totem cadastrado. Crie um ponto de acesso do tipo <strong>Totem</strong> em "Pontos de
                        acesso", acima, para configurá-lo.
                    </Alert>
                ) : (
                    <TextField
                        select
                        value={idAtivo == null ? '' : String(idAtivo)}
                        onChange={(e) => onSelecionarPonto(e.target.value ? Number(e.target.value) : null)}
                        sx={{ minWidth: { sm: 340 } }}
                        helperText="O que você não alterar neste totem continua igual ao que a loja já usava."
                        slotProps={{
                            select: {
                                displayEmpty: true,
                                renderValue: (valor) => {
                                    const ponto = pontos.find((p) => String(p.id) === valor)
                                    return ponto ? rotuloPonto(ponto) : 'Selecione um totem'
                                },
                            },
                        }}
                    >
                        {pontos.map((p) => (
                            <MenuItem key={p.id} value={String(p.id)}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>{rotuloPonto(p)}</span>
                                    {!p.ativo && <Chip size="small" label="Inativo" />}
                                    {p.id in overridesPontos && <Chip size="small" color="warning" label="Não salvo" />}
                                </Stack>
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Secao>

            {pontoSelecionado && (
                <>
                    <Secao
                        icone={<SlideshowIcon />}
                        titulo="Modo TOTEM"
                        descricao="Imagens de fundo da proteção de tela deste totem (quando ele fica ocioso)."
                    >
                        <UploadImagensTotem
                            valor={ler('imagensTotem') as string[] | null}
                            onChange={(v) => escrever({ imagensTotem: v })}
                        />
                    </Secao>

                    <Secao
                        icone={<PrintIcon />}
                        titulo="Impressão do pedido"
                        descricao="O que sai no papel quando o cliente finaliza o pedido neste totem. Nada marcado, nada é impresso."
                    >
                        <Stack spacing={1.5}>
                            <OpcaoImpressaoTotem
                                marcada={impressao.imprimirSenha}
                                aoMudar={(v) => setImpressao({ imprimirSenha: v })}
                                titulo="Imprimir senha"
                                ajuda="O número que o cliente leva para acompanhar o pedido no balcão."
                            />
                            <OpcaoImpressaoTotem
                                marcada={impressao.imprimirResumo}
                                aoMudar={(v) => setImpressao({ imprimirResumo: v })}
                                titulo="Imprimir resumo do pedido"
                                ajuda="Os itens e o total, sem valor fiscal."
                            />
                            <OpcaoImpressaoTotem
                                marcada={impressao.nota !== 'NAO'}
                                aoMudar={(v) => setImpressao({ nota: v ? 'SEMPRE' : 'NAO' })}
                                titulo="Gerar e imprimir nota fiscal (NFC-e)"
                                ajuda="Marcado, a NFC-e é emitida na SEFAZ em todo pedido deste totem. Desmarcado, este totem não emite nota."
                            />

                            {/* Quadro próprio da nota: o emissor vale para as duas opções de impressão, então fica separado dos rádios. */}
                            {impressao.nota !== 'NAO' && (
                                <Paper variant="outlined" sx={{ ml: { xs: 0, sm: 5 }, p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
                                    <Typography id="impressao-nota-totem" variant="subtitle2" sx={{ mb: 1 }}>
                                        Impressão da nota
                                    </Typography>

                                    <RadioGroup
                                        aria-labelledby="impressao-nota-totem"
                                        value={impressao.nota}
                                        onChange={(_e, valor) => setImpressao({ nota: valor as TotemNota })}
                                    >
                                        <FormControlLabel
                                            value="SEMPRE"
                                            control={<Radio />}
                                            sx={{ alignItems: 'flex-start', mb: 1 }}
                                            label={
                                                <Box sx={{ pt: 0.75 }}>
                                                    <Typography>Imprimir sempre</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        A nota sai no papel em todo pedido.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <FormControlLabel
                                            value="PERGUNTAR"
                                            control={<Radio />}
                                            sx={{ alignItems: 'flex-start' }}
                                            label={
                                                <Box sx={{ pt: 0.75 }}>
                                                    <Typography>Perguntar ao cliente se quer imprimir</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        A nota é emitida do mesmo jeito. Depois de autorizada, o totem pergunta se o
                                                        cliente quer a nota impressa.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </RadioGroup>

                                    <OpcaoImpressaoTotem
                                        marcada={perguntaCpf}
                                        aoMudar={(v) => escrever({ totemPerguntarCpfNota: v })}
                                        titulo="Perguntar o CPF na nota"
                                        ajuda="Desmarcado, o totem não pergunta: a nota leva o CPF da identificação, se o cliente informou, ou sai sem CPF."
                                    />

                                    <Divider sx={{ my: 2.5 }} />

                                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                        Emissão
                                    </Typography>

                                    <Box sx={{ maxWidth: { md: 480 } }}>
                                        <CampoUsuarioNfce
                                            idConfig={idConfig}
                                            valor={(ler('totemUsuarioNfce') as string | null | undefined) ?? null}
                                            onChange={(username) => escrever({ totemUsuarioNfce: username })}
                                            ajuda="Usuário do Sischef com permissão fiscal que assina a nota — vale para as duas opções acima."
                                        />
                                    </Box>
                                </Paper>
                            )}

                            <OpcaoImpressaoTotem
                                marcada={comprovante}
                                aoMudar={(v) => escrever({ totemImprimirComprovanteTef: v })}
                                titulo="Imprimir comprovante do cartão"
                                ajuda="A via do cliente da maquininha, quando o pagamento é feito no totem."
                            />

                            {/* Largura inteira: espremido o aviso vira um paredão de linhas curtas. */}
                            {(totemImprimeAlgo(impressao) || comprovante) && (
                                <Box sx={{ pt: 1 }}>
                                    <AvisoPinDispositivo
                                        comoAbrir
                                        titulo="A impressora é configurada no próprio totem"
                                        intro="Escolha a impressora na tela de configuração local do aparelho, protegida por PIN."
                                    />
                                </Box>
                            )}
                        </Stack>
                    </Secao>

                    <Secao
                        icone={<RestaurantIcon />}
                        titulo="Consumir no local ou para levar"
                        descricao="O tipo escolhido vai no pedido e aparece no card do PDV. O cardápio de cada consumo é ajustado em Visibilidade dos itens."
                    >
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={consumo}
                            onChange={(_e, v: ModoConsumoTotem | null) => v && escrever({ totemConsumo: v })}
                            sx={{ flexWrap: 'wrap' }}
                        >
                            <ToggleButton value="AMBOS">Perguntar ao cliente</ToggleButton>
                            <ToggleButton value="LOCAL">Só {ROTULO_CONSUMO.LOCAL.toLowerCase()}</ToggleButton>
                            <ToggleButton value="LEVAR">Só {ROTULO_CONSUMO.LEVAR.toLowerCase()}</ToggleButton>
                        </ToggleButtonGroup>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            {consumo === 'AMBOS'
                                ? 'Este totem abre perguntando onde o cliente vai consumir, antes do cardápio.'
                                : 'Este totem vai direto ao cardápio: a escolha já fica definida e o cliente não é perguntado.'}
                        </Typography>
                    </Secao>

                    <Secao
                        icone={<NotificationsActiveIcon />}
                        titulo="Pager (localizador)"
                        descricao="Se a casa entrega um pager ao cliente, o totem pede o número e ele sai na comanda de produção."
                    >
                        <Stack spacing={2} sx={{ maxWidth: 420 }}>
                            <FormControlLabel
                                control={<Switch checked={pagerAtivo} onChange={(_e, v) => escrever({ totemPagerAtivo: v })} />}
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        Pedir o número do pager no totem, antes do pagamento.
                                    </Typography>
                                }
                                sx={{ alignItems: 'flex-center', m: 0 }}
                            />

                            {pagerAtivo && (
                                <>
                                    <Typography variant="body2" color="text.secondary">
                                        Intervalo de numeração dos pagers deste totem.
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                label="Começa em"
                                                type="number"
                                                fullWidth
                                                value={(ler('totemPagerMinimo') as number | null | undefined) ?? ''}
                                                onChange={(e) => escrever({ totemPagerMinimo: e.target.value ? Number(e.target.value) : null })}
                                                slotProps={{ htmlInput: { min: 1, max: 9999 } }}
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                label="Até"
                                                type="number"
                                                fullWidth
                                                value={(ler('totemPagerMaximo') as number | null | undefined) ?? ''}
                                                onChange={(e) => escrever({ totemPagerMaximo: e.target.value ? Number(e.target.value) : null })}
                                                slotProps={{ htmlInput: { min: 1, max: 9999 } }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Typography variant="caption" color="text.secondary">
                                        Em branco, o totem aceita qualquer número. Preencher evita o cliente digitar 999 num
                                        conjunto que vai até 30.
                                    </Typography>

                                    <Alert severity="info">
                                        Com o pager ligado, informar o número é <strong>obrigatório</strong> neste totem. O
                                        controle de quem está com cada pager é <strong>da loja</strong>: se houver mais de um
                                        aparelho, eles dividem o mesmo conjunto de pagers. Ao digitar um número já entregue a
                                        outro cliente, o totem avisa, mas não bloqueia, para um pedido que ninguém fechou não
                                        travar a venda.
                                    </Alert>
                                </>
                            )}
                        </Stack>
                    </Secao>

                    <Secao icone={<PaidIcon />} titulo="Pedido mínimo" descricao="Valor mínimo para pedidos neste totem.">
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <CampoValorReais
                                    label="Pedido mínimo (totem)"
                                    valor={valorMinimo}
                                    onChange={(v) => escrever({ valorMinimoTotem: v })}
                                    ajuda="Deixe 0 para não exigir valor mínimo."
                                />
                            </Grid>
                        </Grid>
                    </Secao>
                </>
            )}

            <AvisoPinDispositivo comoAbrir />
        </GateModo>
    )
}
